import React, { useEffect, useRef } from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import RevealText from '../RevealText';
import { useT } from '../../i18n/LanguageContext';

/**
 * Global Network — replicates the silver rotating globe segment
 * from https://stiggroup.com/stig-rock/index.php
 * ("Internationale Struktur. Klare Vision.").
 *
 * Full-bleed background video (silver globe with engraved continents +
 * Stig diamond mark embedded in the surface). Overlay: overline, large
 * Stig-style stacked headline, body.
 */
export default function SectionGlobalNetwork() {
  const { t } = useT();
  const g = t.globalNetwork;
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Force play even if Chrome's autoplay is hesitant. Muted + playsInline
    // satisfies all modern browser policies.
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    // Try once more once it is in view, in case the first attempt errored.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) tryPlay();
      },
      { threshold: 0.1 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <SectionShell id="global-network" fullBleed>
      <div className="relative w-full h-screen overflow-hidden">
        <video
          ref={videoRef}
          data-testid="global-network-video"
          data-slide-persist="true"
          className="absolute inset-0 w-full h-full object-cover"
          src="/video/stig-globe.mp4"
          poster="/video/stig-globe-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden
        />

        {/* Right-side gradient for legibility */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 35%, rgba(0,0,0,0.75) 75%, rgba(0,0,0,0.94) 100%)',
          }}
        />

        <div className="absolute inset-0 mx-auto max-w-[1600px] px-6 md:px-12 lg:px-20 flex items-center">
          <div className="grid grid-cols-12 w-full">
            <div className="col-span-12 md:col-span-6 md:col-start-7 text-left">
              <Reveal>
                <div className="overline mb-8 text-white/70">{g.overline}</div>
              </Reveal>
              <Reveal delay={150}>
                <h2
                  data-testid="global-network-title"
                  className="stig-headline compact font-display font-light uppercase mb-10"
                >
                  {g.titleLines.map((line, i) => (
                    <RevealText key={`${line}-${i}`} as="span" delay={i * 220} stagger={32} className="block">
                      {line}
                    </RevealText>
                  ))}
                </h2>
              </Reveal>
              <Reveal delay={300}>
                <p className="font-ui text-base md:text-lg text-white/75 leading-relaxed max-w-[52ch] font-light">
                  {g.body}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
